import { ArrowClockwise, Funnel, Info, SpinnerGap, WarningCircle, X } from '@phosphor-icons/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui-kit/button';
import { Input } from '@/components/ui-kit/input';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui-kit/sheet';
import { Textarea } from '@/components/ui-kit/textarea';
import { useToast } from '@/hooks/use-toast';
import { SegmentAiAssistant } from './segment-ai-assistant';
import { SegmentRuleCanvas } from './segment-rule-canvas';
import { isSegmentAiConfigured } from '../services/segment-ai.service';
import type { SegmentAiDraft } from '../types/segment-ai.types';
import {
  useCreateSegment,
  useGetSegmentFields,
  usePreviewSegmentRules,
  useUpdateSegment,
} from '../hooks/use-segments';
import type {
  Segment,
  SegmentContactSummary,
  SegmentFieldDefinition,
  SegmentPreviewResult,
  SegmentRuleGroup,
  UpdateSegmentInput,
} from '../types/segments.types';
import {
  extractQuotedFieldFromRulesMessage,
  extractRuleGroupIndexFromMessage,
  getFieldErrorsFromUnknown,
  getSegmentSessionGuidance,
  SEGMENT_PREVIEW_TRUNCATED_MESSAGE,
} from '../utils/segment-api-errors';
import { createEmptyRuleGroup, emptyConditionForField, isRuleTreeComplete } from '../utils/segment-rule-tree';

export type SegmentLoadState = 'loading' | 'ready' | 'error' | 'not_found';

export type SegmentBuilderDrawerProps = {
  open: boolean;
  mode: 'create' | 'edit';
  segmentId: string | null;
  segment?: Segment | null;
  segmentLoadState?: SegmentLoadState;
  onRetryLoad?: () => void;
  onOpenChange: (open: boolean) => void;
  onSaved: (segmentId: string) => void;
};

const PREVIEW_DEBOUNCE_MS = 350;
const PREVIEW_SAMPLE_SIZE = 6;
const MEMBERSHIP_CAVEAT =
  "Membership isn't permission to send — subscribed and suppressed status is checked again at send time.";

const cloneRuleGroup = (group: SegmentRuleGroup): SegmentRuleGroup =>
  JSON.parse(JSON.stringify(group)) as SegmentRuleGroup;

const snapshotForm = (name: string, description: string, rules: SegmentRuleGroup) =>
  JSON.stringify({ name, description, rules });

const createDraftRules = (fields: SegmentFieldDefinition[]): SegmentRuleGroup => {
  const first = fields[0];
  if (!first) return createEmptyRuleGroup();
  return {
    combinator: 'and',
    conditions: [emptyConditionForField(first)],
    groups: [],
  };
};

const sampleDisplayName = (contact: SegmentContactSummary) => {
  const fullName = [contact.firstName, contact.lastName].filter(Boolean).join(' ').trim();
  return fullName || contact.email || contact.itemId;
};

const sampleInitials = (contact: SegmentContactSummary) => {
  const first = contact.firstName?.trim()?.[0];
  const last = contact.lastName?.trim()?.[0];
  if (first || last) return `${first ?? ''}${last ?? ''}`.toUpperCase();
  const fallback = contact.email?.trim()?.[0] || contact.itemId?.[0] || '?';
  return fallback.toUpperCase();
};

const buildEditInput = (
  name: string,
  description: string,
  rules: SegmentRuleGroup,
  originalName: string,
  originalDescription: string,
  originalRules: SegmentRuleGroup
): UpdateSegmentInput => {
  const input: UpdateSegmentInput = {};
  if (name !== originalName) input.name = name;
  if (description !== originalDescription) input.description = description;
  if (JSON.stringify(rules) !== JSON.stringify(originalRules)) input.rules = rules;
  return input;
};

export const SegmentBuilderDrawer = ({
  open,
  mode,
  segmentId,
  segment,
  segmentLoadState = 'ready',
  onRetryLoad,
  onOpenChange,
  onSaved,
}: SegmentBuilderDrawerProps) => {
  const { toast } = useToast();
  const {
    data: fields = [],
    isError: isFieldsError,
    error: fieldsError,
    refetch: refetchFields,
  } = useGetSegmentFields({ enabled: open });
  const { mutateAsync: createSegment, isPending: isCreating } = useCreateSegment();
  const { mutateAsync: updateSegment, isPending: isUpdating } = useUpdateSegment();
  const { mutateAsync: previewRules } = usePreviewSegmentRules();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [rules, setRules] = useState<SegmentRuleGroup>(createEmptyRuleGroup);
  const [nameError, setNameError] = useState('');
  const [rulesError, setRulesError] = useState('');
  const [highlightedFields, setHighlightedFields] = useState<string[]>([]);
  const [highlightedGroupIndexes, setHighlightedGroupIndexes] = useState<number[]>([]);
  const [showDiscardBanner, setShowDiscardBanner] = useState(false);
  const [preview, setPreview] = useState<SegmentPreviewResult | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);

  const initialSnapshotRef = useRef<string | null>(null);
  const originalNameRef = useRef('');
  const originalDescriptionRef = useRef('');
  const originalRulesRef = useRef<SegmentRuleGroup>(createEmptyRuleGroup());
  const boundSessionRef = useRef<string | null>(null);
  const didSeedCreateRulesRef = useRef(false);
  const previewGenerationRef = useRef(0);
  const nameRef = useRef(name);
  const descriptionRef = useRef(description);
  nameRef.current = name;
  descriptionRef.current = description;

  const invalidatePreview = useCallback(() => {
    previewGenerationRef.current += 1;
  }, []);

  const isSaving = isCreating || isUpdating;
  const isWaitingForSegment = segmentLoadState === 'loading';
  const isSegmentLoadFailed = segmentLoadState === 'error' || segmentLoadState === 'not_found';
  const rulesComplete = isRuleTreeComplete(rules);

  useEffect(() => {
    if (!open) {
      boundSessionRef.current = null;
      didSeedCreateRulesRef.current = false;
      return;
    }
    if (isWaitingForSegment || isSegmentLoadFailed) return;
    if (mode === 'edit' && !segment) return;

    const session = `${mode}:${segmentId ?? 'new'}:${segment?.itemId ?? ''}`;
    if (boundSessionRef.current === session) return;

    const nextName = mode === 'edit' ? (segment?.name ?? '') : '';
    const nextDescription = mode === 'edit' ? (segment?.description ?? '') : '';
    const nextRules =
      mode === 'edit'
        ? segment?.rules
          ? cloneRuleGroup(segment.rules)
          : createEmptyRuleGroup()
        : createDraftRules(fields);

    setName(nextName);
    setDescription(nextDescription);
    setRules(nextRules);
    setNameError('');
    setRulesError('');
    setHighlightedFields([]);
    setHighlightedGroupIndexes([]);
    setShowDiscardBanner(false);
    invalidatePreview();
    setPreview(null);
    setPreviewError(null);
    setIsPreviewLoading(false);
    initialSnapshotRef.current = snapshotForm(nextName, nextDescription, nextRules);
    originalNameRef.current = nextName;
    originalDescriptionRef.current = nextDescription;
    originalRulesRef.current = cloneRuleGroup(nextRules);
    boundSessionRef.current = session;
    didSeedCreateRulesRef.current = mode === 'edit' || Boolean(fields[0]);
  }, [fields, invalidatePreview, isSegmentLoadFailed, isWaitingForSegment, mode, open, segment, segmentId]);

  useEffect(() => {
    if (!open || mode !== 'create' || didSeedCreateRulesRef.current) return;
    if (!fields[0]) return;

    const nextRules = createDraftRules(fields);
    setRules(nextRules);
    didSeedCreateRulesRef.current = true;
    initialSnapshotRef.current = snapshotForm(nameRef.current, descriptionRef.current, nextRules);
    originalRulesRef.current = cloneRuleGroup(nextRules);
  }, [fields, mode, open]);

  const runPreview = useCallback(
    async (tree: SegmentRuleGroup) => {
      if (!isRuleTreeComplete(tree)) return;
      const generation = ++previewGenerationRef.current;
      setIsPreviewLoading(true);
      setPreviewError(null);
      try {
        const result = await previewRules({ rules: tree, sampleSize: PREVIEW_SAMPLE_SIZE });
        if (generation !== previewGenerationRef.current) return;
        setPreview(result);
      } catch (error) {
        if (generation !== previewGenerationRef.current) return;
        setPreview(null);
        setPreviewError(getSegmentSessionGuidance(error) ?? 'Could not load the preview.');
      } finally {
        if (generation === previewGenerationRef.current) {
          setIsPreviewLoading(false);
        }
      }
    },
    [previewRules]
  );

  useEffect(() => {
    if (!open || !rulesComplete) {
      invalidatePreview();
      setPreview(null);
      setPreviewError(null);
      setIsPreviewLoading(false);
      return;
    }

    invalidatePreview();
    const timer = window.setTimeout(() => {
      void runPreview(rules);
    }, PREVIEW_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [invalidatePreview, open, rules, rulesComplete, runPreview]);

  const currentSnapshot = useCallback(
    () => snapshotForm(name, description, rules),
    [description, name, rules]
  );

  const requestClose = () => {
    if (initialSnapshotRef.current !== null && initialSnapshotRef.current !== currentSnapshot()) {
      setShowDiscardBanner(true);
      return;
    }
    onOpenChange(false);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      requestClose();
      return;
    }
    onOpenChange(true);
  };

  const applyFieldErrors = (error: unknown) => {
    const fieldErrors = getFieldErrorsFromUnknown(error);
    const nameMessages = fieldErrors.Name ?? [];
    const rulesMessages = fieldErrors.Rules ?? [];
    if (nameMessages.length === 0 && rulesMessages.length === 0) return false;

    if (nameMessages[0]) setNameError(nameMessages[0]);
    const quoted = rulesMessages
      .map(extractQuotedFieldFromRulesMessage)
      .filter((field): field is string => Boolean(field));
    setHighlightedFields(quoted);

    const groupIndexes = rulesMessages
      .map(extractRuleGroupIndexFromMessage)
      .filter((index): index is number => index !== null);
    setHighlightedGroupIndexes(groupIndexes);

    const genericRules = rulesMessages.filter(
      (message) =>
        !extractQuotedFieldFromRulesMessage(message) &&
        extractRuleGroupIndexFromMessage(message) === null
    );
    if (genericRules[0]) setRulesError(genericRules[0]);
    else if (quoted.length > 0 || groupIndexes.length > 0) setRulesError(rulesMessages[0] ?? '');
    return true;
  };

  const handleApplyAiDraft = (draft: SegmentAiDraft) => {
    if (!nameRef.current.trim() && draft.name) {
      setName(draft.name);
      setNameError('');
    }
    if (!descriptionRef.current.trim() && draft.description) {
      setDescription(draft.description);
    }
    setRules(draft.rules);
    setRulesError('');
    setHighlightedFields([]);
    setHighlightedGroupIndexes([]);
  };

  const handleSave = async () => {
    const trimmedName = name.trim();
    const trimmedDescription = description.trim();
    if (!trimmedName) {
      setNameError('Name is required.');
      return;
    }
    if (!isRuleTreeComplete(rules)) {
      setRulesError('Add at least one complete condition.');
      return;
    }

    setNameError('');
    setRulesError('');
    setHighlightedFields([]);
    setHighlightedGroupIndexes([]);

    try {
      let savedId = segmentId ?? '';
      if (mode === 'create') {
        const result = await createSegment({
          name: trimmedName,
          description: trimmedDescription || undefined,
          rules,
        });
        savedId = result.itemId;
      } else if (segmentId) {
        const input = buildEditInput(
          trimmedName,
          trimmedDescription,
          rules,
          originalNameRef.current,
          originalDescriptionRef.current,
          originalRulesRef.current
        );
        if (Object.keys(input).length > 0) {
          await updateSegment({ segmentId, input });
        }
        savedId = segmentId;
      }

      toast({
        variant: 'success',
        title: mode === 'create' ? 'Segment created' : 'Segment updated',
        description: 'Your segment rules were saved.',
      });
      onSaved(savedId);
      onOpenChange(false);
    } catch (error) {
      if (applyFieldErrors(error)) return;
      toast({
        variant: 'destructive',
        title: mode === 'create' ? 'Failed to create segment' : 'Failed to update segment',
        description:
          getSegmentSessionGuidance(error) ??
          (error instanceof Error ? error.message : 'Please try again.'),
      });
    }
  };

  const drawerTitle = mode === 'create' ? 'New segment' : segment?.name?.trim() || 'Edit segment';
  const previewEnabled = open && rulesComplete;

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent
        side="right"
        overlayClassName="bg-black/40"
        className="flex w-full max-w-[880px] flex-col gap-0 overflow-hidden border-neutral-200 bg-white p-0 shadow-e4 sm:max-w-[880px] [&>button]:hidden"
      >
        <div className="flex h-14 shrink-0 items-center justify-between gap-3 border-b border-neutral-200 px-5">
          <div className="min-w-0">
            <SheetTitle className="truncate text-base font-semibold text-neutral-900">
              {drawerTitle}
            </SheetTitle>
            <SheetDescription className="sr-only">
              Create or edit a dynamic audience segment.
            </SheetDescription>
          </div>
          <button
            type="button"
            className="inline-flex size-8 shrink-0 items-center justify-center rounded-md text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800"
            aria-label="Close"
            onClick={requestClose}
          >
            <X className="size-[18px]" aria-hidden />
          </button>
        </div>

        {showDiscardBanner ? (
          <div className="flex shrink-0 items-center justify-between gap-3 border-b border-neutral-200 bg-warning-background px-5 py-3">
            <span className="text-xs text-neutral-800">
              Discard this segment? Your unsaved changes will be lost.
            </span>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 px-3 text-[13px]"
                onClick={() => setShowDiscardBanner(false)}
              >
                Keep editing
              </Button>
              <Button
                type="button"
                size="sm"
                className="h-8 bg-destructive px-3 text-[13px] text-white hover:bg-destructive/90"
                onClick={() => {
                  setShowDiscardBanner(false);
                  onOpenChange(false);
                }}
              >
                Discard
              </Button>
            </div>
          </div>
        ) : null}

        <div className="min-h-0 flex-1 overflow-y-auto bg-neutral-50 p-5">
          {isWaitingForSegment ? (
            <div className="flex h-full items-center justify-center text-sm text-neutral-500">
              <SpinnerGap className="mr-2 size-4 animate-spin" aria-hidden />
              Loading segment…
            </div>
          ) : isSegmentLoadFailed ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
              <WarningCircle className="size-8 text-error" aria-hidden />
              <p className="text-sm text-neutral-800">
                {segmentLoadState === 'not_found'
                  ? 'This segment could not be found.'
                  : 'Could not load this segment.'}
              </p>
              {segmentLoadState === 'error' && onRetryLoad ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 text-primary-600"
                  onClick={onRetryLoad}
                >
                  Try again
                </Button>
              ) : null}
            </div>
          ) : (
            <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-[minmax(0,1fr)_300px]">
              <div className="space-y-5 rounded-lg border border-neutral-200 bg-white p-5 shadow-e1">
                {mode === 'create' && !isFieldsError && isSegmentAiConfigured() ? (
                  <SegmentAiAssistant fields={fields} onApply={handleApplyAiDraft} />
                ) : null}

                <div>
                  <label
                    htmlFor="segment-builder-name"
                    className="mb-1.5 block text-[13px] font-medium text-neutral-900"
                  >
                    Name <span className="text-destructive">*</span>
                  </label>
                  <Input
                    id="segment-builder-name"
                    value={name}
                    className="h-10 border-neutral-300"
                    aria-invalid={Boolean(nameError)}
                    onChange={(event) => {
                      setName(event.target.value);
                      if (nameError) setNameError('');
                    }}
                  />
                  {nameError ? (
                    <p className="mt-1 text-xs text-destructive" role="alert">
                      {nameError}
                    </p>
                  ) : (
                    <p className="mt-1 text-xs text-neutral-500">Must be unique across your segments.</p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="segment-builder-description"
                    className="mb-1.5 block text-[13px] font-medium text-neutral-900"
                  >
                    Description
                  </label>
                  <Textarea
                    id="segment-builder-description"
                    value={description}
                    rows={3}
                    className="resize-none border-neutral-300"
                    onChange={(event) => setDescription(event.target.value)}
                  />
                </div>

                <div>
                  <div className="mb-3">
                    <h3 className="text-sm font-semibold text-neutral-900">Rules</h3>
                    <p className="mt-0.5 text-xs text-neutral-500">
                      Contacts update automatically when they match these filters.
                    </p>
                  </div>
                  {isFieldsError ? (
                    <div className="rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-8 text-center">
                      <WarningCircle className="mx-auto size-7 text-error" aria-hidden />
                      <p className="mt-2 text-sm font-medium text-neutral-900">
                        Could not load segment fields
                      </p>
                      <p className="mt-1 text-xs text-neutral-500">
                        {getSegmentSessionGuidance(fieldsError) ??
                          'Something went wrong on our end. Try again.'}
                      </p>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="mt-3 h-8 border-neutral-200"
                        onClick={() => void refetchFields()}
                      >
                        <ArrowClockwise className="size-4" aria-hidden />
                        Retry
                      </Button>
                    </div>
                  ) : (
                    <SegmentRuleCanvas
                      value={rules}
                      onChange={(next) => {
                        setRules(next);
                        if (rulesError) setRulesError('');
                        if (highlightedFields.length > 0) setHighlightedFields([]);
                        if (highlightedGroupIndexes.length > 0) setHighlightedGroupIndexes([]);
                      }}
                      fields={fields}
                      highlightedFields={highlightedFields}
                      highlightedGroupIndexes={highlightedGroupIndexes}
                    />
                  )}
                  {rulesError ? (
                    <p className="mt-2 text-xs text-destructive" role="alert">
                      {rulesError}
                    </p>
                  ) : null}
                </div>
              </div>

              <aside className="space-y-4 rounded-lg border border-neutral-200 bg-white p-5 shadow-e1 lg:sticky lg:top-0">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <h3 className="text-sm font-semibold text-neutral-900">Preview</h3>
                    <p className="mt-0.5 text-xs text-neutral-500">Updates as rules change</p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-8 text-primary-600"
                    aria-label="Refresh preview"
                    disabled={!previewEnabled || isPreviewLoading}
                    onClick={() => void runPreview(rules)}
                  >
                    <ArrowClockwise
                      className={`size-4 ${isPreviewLoading ? 'animate-spin' : ''}`}
                      aria-hidden
                    />
                  </Button>
                </div>

                {!previewEnabled ? (
                  <div className="rounded-lg bg-neutral-50 px-3 py-6 text-center">
                    <Funnel className="mx-auto size-6 text-neutral-400" aria-hidden />
                    <p className="mt-2 text-xs text-neutral-500">
                      Add a complete rule to preview matching contacts.
                    </p>
                  </div>
                ) : previewError ? (
                  <div className="rounded-lg bg-error-background px-3 py-3">
                    <p className="text-xs text-error">{previewError}</p>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="mt-1 h-7 px-0 text-error"
                      onClick={() => void runPreview(rules)}
                    >
                      Try again
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="rounded-lg bg-primary-50 px-3 py-3">
                      <span className="block text-xs text-primary-900">Matching contacts</span>
                      <strong className="mt-0.5 block text-2xl font-semibold tabular-nums text-primary-900">
                        {isPreviewLoading || !preview
                          ? '…'
                          : preview.truncated
                            ? `At least ${preview.matchCount.toLocaleString()}`
                            : preview.matchCount.toLocaleString()}
                      </strong>
                      {preview && !isPreviewLoading ? (
                        <p className="mt-1 text-xs text-primary-900">
                          {preview.truncated ? 'At least ' : ''}
                          {(preview.sendableCount ?? 0).toLocaleString()} can receive email
                        </p>
                      ) : null}
                      {preview?.truncated ? (
                        <p className="mt-2 text-xs text-warning">{SEGMENT_PREVIEW_TRUNCATED_MESSAGE}</p>
                      ) : null}
                      {preview && !isPreviewLoading && preview.matchCount === 0 ? (
                        <p className="mt-2 text-xs text-neutral-600">
                          No contacts match these rules yet — broaden a condition.
                        </p>
                      ) : null}
                    </div>
                    <div className="space-y-1.5">
                      {(preview?.sample ?? []).map((contact) => (
                        <div
                          key={contact.itemId}
                          className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-neutral-50"
                        >
                          <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-primary-50 text-[11px] font-semibold text-primary-600">
                            {sampleInitials(contact)}
                          </div>
                          <div className="min-w-0">
                            <p className="truncate text-xs font-medium text-neutral-900">
                              {sampleDisplayName(contact)}
                            </p>
                            <p className="truncate text-[11px] text-neutral-500">
                              {contact.email || 'No email'}
                            </p>
                          </div>
                        </div>
                      ))}
                      {!isPreviewLoading && preview && preview.sample.length === 0 ? (
                        <p className="py-3 text-center text-xs text-neutral-500">
                          No contacts match these rules.
                        </p>
                      ) : null}
                    </div>
                    {preview ? (
                      <div className="flex items-start gap-2 rounded-lg bg-neutral-50 px-3 py-2.5">
                        <Info className="mt-0.5 size-4 shrink-0 text-neutral-600" aria-hidden />
                        <p className="text-xs leading-[18px] text-neutral-700">{MEMBERSHIP_CAVEAT}</p>
                      </div>
                    ) : null}
                  </>
                )}
              </aside>
            </div>
          )}
        </div>

        <div className="flex shrink-0 items-center justify-end gap-3 border-t border-neutral-200 px-5 py-3">
          {isSegmentLoadFailed ? (
            <Button
              type="button"
              className="h-10 bg-primary-600 px-4 text-white hover:bg-primary-700"
              onClick={() => onOpenChange(false)}
            >
              Close
            </Button>
          ) : (
            <>
              <Button
                type="button"
                variant="ghost"
                className="h-10 px-4 text-neutral-900"
                disabled={isSaving}
                onClick={requestClose}
              >
                Cancel
              </Button>
              <Button
                type="button"
                className="h-10 bg-primary-600 px-4 text-white hover:bg-primary-700"
                disabled={isSaving || isWaitingForSegment}
                onClick={() => void handleSave()}
              >
                {isSaving ? (
                  <>
                    <SpinnerGap className="size-4 animate-spin" aria-hidden />
                    Saving…
                  </>
                ) : mode === 'edit' ? (
                  'Save changes'
                ) : (
                  'Save'
                )}
              </Button>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
